import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useDigestConfigList,
  useDigestConfigDelete,
  useDigestGenerate,
  useDigestResultList,
} from "../hooks";
import { useReadUser } from "../services/api";
import type { DigestConfig, DigestResult } from "../types/digest";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Modal,
  ModalDialog,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PublicIcon from "@mui/icons-material/Public";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";
import DigestTemplatePicker from "../components/DigestTemplatePicker";
import type { DigestTemplate } from "../digestTemplates";

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Convert UTC HH:MM + day to local timezone for display. */
function utcToLocalDisplay(
  utcTime: string,
  utcDay: number | null,
): { time: string; day: number | null } {
  const [h, m] = utcTime.split(":").map(Number);
  const now = new Date();
  const utcDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m),
  );
  const localH = String(utcDate.getHours()).padStart(2, "0");
  const localM = String(utcDate.getMinutes()).padStart(2, "0");
  const dayShift = utcDate.getDate() - now.getDate();
  const adjustedDay = utcDay !== null ? (utcDay + dayShift + 7) % 7 : null;
  return { time: `${localH}:${localM}`, day: adjustedDay };
}

function formatSchedule(config: DigestConfig): string {
  if (!config.interval) return "Manual";
  const local = utcToLocalDisplay(
    config.anchor_time || "09:00",
    config.anchor_day ?? null,
  );
  switch (config.interval) {
    case "daily":
      return `Daily at ${local.time}`;
    case "weekly":
      return `Weekly, ${DAYS_OF_WEEK[local.day ?? 0]} ${local.time}`;
    case "biweekly":
      return `Bi-weekly, ${DAYS_OF_WEEK[local.day ?? 0]} ${local.time}`;
    case "monthly":
      return `Monthly, day ${config.anchor_day ?? 1} at ${local.time}`;
    default:
      return "Manual";
  }
}

const DigestList: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();

  const [configs, setConfigs] = useState<DigestConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [latestResults, setLatestResults] = useState<
    Record<string, DigestResult | null>
  >({});

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const readUser = useReadUser();
  const fetchConfigs = useDigestConfigList();
  const deleteConfig = useDigestConfigDelete();
  const generateDigest = useDigestGenerate();
  const fetchResults = useDigestResultList();

  useEffect(() => {
    readUser()
      .then((u) => setCurrentUserId(u.id))
      .catch(() => {});
  }, [readUser]);

  const loadConfigs = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchConfigs(workspace.id);
      setConfigs(data);

      // Fetch latest result for each config
      const resultMap: Record<string, DigestResult | null> = {};
      for (const config of data) {
        try {
          const results = await fetchResults(workspace.id, config.id);
          resultMap[config.id] = results.length > 0 ? results[0] : null;
        } catch {
          resultMap[config.id] = null;
        }
      }
      setLatestResults(resultMap);
    } catch (err) {
      console.error("Error loading digest configs:", err);
      setError("Failed to load digest configurations.");
    } finally {
      setLoading(false);
    }
  }, [workspace, fetchConfigs, fetchResults]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleDelete = (configId: string) => {
    setDeleteConfirmId(configId);
  };

  const confirmDelete = async () => {
    if (!workspace?.id || !deleteConfirmId) return;
    const configId = deleteConfirmId;
    setDeleteConfirmId(null);

    try {
      setDeletingId(configId);
      await deleteConfig(workspace.id, configId);
      setConfigs((prev) => prev.filter((c) => c.id !== configId));
    } catch (err) {
      console.error("Error deleting digest config:", err);
      setError("Failed to delete configuration.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerate = async (configId: string) => {
    if (!workspace?.id) return;
    try {
      setGeneratingId(configId);
      setError(null);
      const result = await generateDigest(workspace.id, configId);
      setLatestResults((prev) => ({ ...prev, [configId]: result }));
      // Navigate to the result
      navigate(
        `/workspace/${workspace.id}/bkg/catalyst-events/digests/${configId}/results/${result.id}`,
      );
    } catch (err) {
      console.error("Error generating digest:", err);
      setError("Failed to generate digest. Please try again.");
    } finally {
      setGeneratingId(null);
    }
  };

  const handleTemplateSelect = (template: DigestTemplate) => {
    if (!workspace?.id) return;
    navigate(
      `/workspace/${workspace.id}/bkg/catalyst-events/digests/new?template=${template.id}`,
    );
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    // Backend returns UTC timestamps without Z suffix -- ensure JS parses as UTC
    const utcStr = dateStr.endsWith("Z") ? dateStr : dateStr + "Z";
    return new Date(utcStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Catalyst Digests</Typography>
        <Tooltip title="What are Catalyst Digests?" placement="bottom">
          <IconButton
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setIntroOpen(true)}
            aria-label="What are Catalyst Digests?"
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <Button
          startDecorator={<AddIcon />}
          onClick={() =>
            navigate(
              `/workspace/${workspace.id}/bkg/catalyst-events/digests/new`,
            )
          }
        >
          New Digest
        </Button>
      </PageHeader>

      <Stack spacing={3}>
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading />
        ) : configs.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={3} sx={{ py: 2 }}>
                <Box>
                  <Typography level="h3">Get your first digest</Typography>
                  <Typography level="body-md" color="neutral">
                    A digest is a prioritised summary of recent catalyst events,
                    generated on a schedule and sent to your inbox.
                  </Typography>
                </Box>
                <DigestTemplatePicker onSelect={handleTemplateSelect} />
                <Box>
                  <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<AddIcon />}
                    onClick={() =>
                      navigate(
                        `/workspace/${workspace.id}/bkg/catalyst-events/digests/new`,
                      )
                    }
                  >
                    Write your own instead
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(340px, 1fr))",
              },
              gap: 2,
            }}
          >
            {configs.map((config) => {
              const latest = latestResults[config.id];
              const isGenerating = generatingId === config.id;
              const isDeleting = deletingId === config.id;
              const isOwner = currentUserId === config.created_by;

              return (
                <Card
                  key={config.id}
                  variant="outlined"
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    {/* Header */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ mb: 1.5 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-lg" noWrap>
                          {config.name}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ flexShrink: 0 }}
                      >
                        {config.is_public && (
                          <Chip
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={
                              <PublicIcon sx={{ fontSize: 14 }} />
                            }
                          >
                            Public
                          </Chip>
                        )}
                        <Chip
                          size="sm"
                          variant="soft"
                          color={config.interval ? "success" : "neutral"}
                        >
                          {config.interval ? "Scheduled" : "Manual"}
                        </Chip>
                      </Stack>
                    </Stack>

                    {/* Filters */}
                    <Box sx={{ mb: 1 }}>
                      {config.event_type_filters.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {config.event_type_filters.slice(0, 3).map((t) => (
                            <Chip key={t} size="sm" variant="outlined">
                              {t}
                            </Chip>
                          ))}
                          {config.event_type_filters.length > 3 && (
                            <Chip size="sm" variant="outlined">
                              +{config.event_type_filters.length - 3} more
                            </Chip>
                          )}
                        </Box>
                      ) : (
                        <Typography level="body-sm" color="neutral">
                          All event types
                        </Typography>
                      )}
                    </Box>

                    {/* Schedule */}
                    <Typography level="body-sm" color="neutral">
                      {formatSchedule(config)}
                    </Typography>

                    {/* Last generated */}
                    <Typography
                      level="body-xs"
                      color="neutral"
                      sx={{ mb: 1.5 }}
                    >
                      Last generated: {formatDate(config.last_generated_at)}
                    </Typography>

                    {/* Spacer pushes actions to bottom */}
                    <Box sx={{ flex: 1 }} />

                    <Divider sx={{ mb: 1.5 }} />

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      {isOwner && (
                        <Button
                          size="sm"
                          variant="solid"
                          startDecorator={<PlayArrowIcon />}
                          onClick={() => handleGenerate(config.id)}
                          disabled={isGenerating}
                          loading={isGenerating}
                        >
                          Generate Now
                        </Button>
                      )}

                      {latest && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          startDecorator={<VisibilityIcon />}
                          onClick={() =>
                            navigate(
                              `/workspace/${workspace.id}/bkg/catalyst-events/digests/${config.id}/results/${latest.id}`,
                            )
                          }
                        >
                          View Latest
                        </Button>
                      )}

                      {latest && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          startDecorator={<HistoryIcon />}
                          onClick={() =>
                            navigate(
                              `/workspace/${workspace.id}/bkg/catalyst-events/digests/${config.id}/results`,
                            )
                          }
                        >
                          History
                        </Button>
                      )}

                      {isOwner && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          startDecorator={<EditIcon />}
                          onClick={() =>
                            navigate(
                              `/workspace/${workspace.id}/bkg/catalyst-events/digests/${config.id}/edit`,
                            )
                          }
                        >
                          Edit
                        </Button>
                      )}

                      {isOwner && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="danger"
                          startDecorator={<DeleteIcon />}
                          onClick={() => handleDelete(config.id)}
                          disabled={isDeleting}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {!loading && configs.length > 0 && (
          <DigestTemplatePicker onSelect={handleTemplateSelect} />
        )}
      </Stack>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />

      <Modal open={introOpen} onClose={() => setIntroOpen(false)}>
        <ModalDialog size="md" variant="outlined" sx={{ maxWidth: 480 }}>
          <DialogTitle>What are Catalyst Digests?</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography level="body-sm">
                A Catalyst Digest automatically collects and prioritises recent
                catalyst events — clinical trial results, regulatory decisions,
                conference presentations, and more — based on a prioritisation
                prompt you define.
              </Typography>
              <Typography level="body-sm">
                Each digest configuration lets you control:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" level="body-sm">
                  <strong>Prioritisation prompt</strong> — describe what matters
                  most (e.g. late-stage oncology readouts).
                </Typography>
                <Typography component="li" level="body-sm">
                  <strong>Event type filters</strong> — narrow to specific
                  categories such as FDA decisions or Phase III results.
                </Typography>
                <Typography component="li" level="body-sm">
                  <strong>Schedule</strong> — run on demand or automatically
                  (daily, weekly, etc.).
                </Typography>
                <Typography component="li" level="body-sm">
                  <strong>Visibility</strong> — keep private or share with your
                  workspace.
                </Typography>
              </Box>
              <Typography level="body-sm">
                Click <strong>New Digest</strong> to create your first
                configuration, then <strong>Generate Now</strong> to produce a
                ranked summary.
              </Typography>
            </Stack>
          </DialogContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button
              variant="solid"
              color="primary"
              onClick={() => setIntroOpen(false)}
            >
              Got it
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <ModalDialog size="md" variant="outlined" sx={{ maxWidth: 400 }}>
          <DialogTitle>Delete Digest</DialogTitle>
          <Typography level="body-md" color="neutral">
            Delete this digest configuration and all its results? This cannot be
            undone.
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setDeleteConfirmId(null)}
            >
              Cancel
            </Button>
            <Button variant="solid" color="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default DigestList;
